import React, { useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LogoProvider } from './contexts/LogoContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { AuthProvider } from './contexts/AuthContext';
import AdminPermissionUpdater from './components/AdminPermissionUpdater';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import NetworkStatus from './components/NetworkStatus';
import backgroundSyncService from './services/backgroundSyncService';
import realtimeDataService from './services/realtimeDataService';
import { initializeAnalytics } from './services/analyticsService';
import firebaseConfig from './config/firebaseConfig';
import AuthGate from './components/AuthGate';

// Import CSS overrides
import './styles/overrides.css';

const App = () => {
  // Add cache detection logic and background sync initialization
  useEffect(() => {
    // TEMPORARY: Disable automatic version checking to prevent refresh loops
    console.log('Automatic version checking is disabled to prevent refresh loops');

    // Initialize Google Analytics
    if (firebaseConfig.measurementId) {
      try {
        initializeAnalytics(firebaseConfig.measurementId);
        console.log('✅ Analytics initialized with measurement ID:', firebaseConfig.measurementId);
      } catch (error) {
        console.error('❌ Failed to initialize analytics:', error);
      }
    } else {
      console.warn('⚠️ Analytics measurement ID not found in Firebase config');
    }

    // Initialize background sync service
    console.log('🔄 Background sync service initialized');
    console.log('📊 Sync queue stats:', backgroundSyncService.getQueueStats());

    // Process any pending sync items
    backgroundSyncService.processQueue().catch(error => {
      console.error('❌ Error processing sync queue:', error);
    });

    // Initialize real-time data service
    console.log('🔴 Real-time data service status:', realtimeDataService.getStatus());
    
    /* 
    // Original version checking code - temporarily disabled
    const checkForUpdates = async () => {
      try {
        // Skip version check if we've recently refreshed
        const lastRefresh = localStorage.getItem('last_refresh_time');
        const currentTime = new Date().getTime();
        
        if (lastRefresh && (currentTime - parseInt(lastRefresh)) < 5 * 60 * 1000) {
          console.log('Skipping version check - recently refreshed');
          return;
        }
        
        // Fetch the current version from the server with cache busting
        const response = await fetch(`/version.txt?t=${currentTime}`);
        
        if (response.ok) {
          const serverVersion = await response.text();
          const clientVersion = document.querySelector('meta[name="app-version"]')?.content || 'unknown';
          
          console.log('Version check - Server:', serverVersion, 'Client:', clientVersion);
          
          // Only compare if both versions are non-empty and the app was previously loaded
          if (serverVersion.trim() && 
              clientVersion.trim() && 
              serverVersion.trim() !== clientVersion.trim() && 
              localStorage.getItem('app_loaded') === 'true' &&
              !localStorage.getItem('version_alert_shown')) {
              
            // Set flag to prevent multiple alerts
            localStorage.setItem('version_alert_shown', 'true');
            
            if (window.confirm('A new version is available. Reload now to update?')) {
              // Record refresh time
              localStorage.setItem('last_refresh_time', currentTime.toString());
              localStorage.removeItem('version_alert_shown');
              
              // Clear cache and reload
              if (window.clearAppCache) {
                window.clearAppCache();
              } else {
                window.location.reload(true);
              }
            } else {
              // User declined the refresh, so wait before asking again
              setTimeout(() => {
                localStorage.removeItem('version_alert_shown');
              }, 60 * 60 * 1000); // Wait 1 hour before asking again
            }
          }
        }
      } catch (error) {
        console.log('Version check failed:', error);
      }
      
      // Mark that the app has been loaded at least once
      localStorage.setItem('app_loaded', 'true');
    };
    
    // Check for updates on load
    checkForUpdates();
    
    // Check for updates periodically, but less frequently
    const intervalId = setInterval(checkForUpdates, 60 * 60 * 1000); // Check every 60 minutes
    
    return () => clearInterval(intervalId);
    */
  }, []);

  return (
    <AccessibilityProvider>
      <AuthProvider>
        <ThemeProvider>
          <LogoProvider>
            <SidebarProvider>
              <NotificationProvider>
                {/* These components will update permissions in Firebase */}
                <AdminPermissionUpdater />
                <PwaInstallPrompt />
                <NetworkStatus />
                {/* AuthGate handles all authentication routing and state management */}
                <AuthGate />
              </NotificationProvider>
            </SidebarProvider>
          </LogoProvider>
        </ThemeProvider>
      </AuthProvider>
    </AccessibilityProvider>
  );
};

export default App;
